import { fg } from '@atlassian/jira-feature-gating';
import type {
	Option,
	Group,
	Options,
} from '@atlassian/jira-issue-field-select-base/src/common/types.tsx';
import type { FormatMessage } from '@atlassian/jira-shared-types/src/general.tsx';
import messages from './messages.tsx';
import type { IssueHistorySearchResults } from './types.tsx';

// If the full URL supplied matches the Jira view issue pattern for the specified site then strip the URL
// to just the issue key. If the URL does not match the pattern then return it unchanged.
const ISSUE_BROWSE_PATH = 'browse/';
const ISSUE_KEY_REGEXP = /^(([A-Z][A-Z0-9]+)-([0-9]+))($|\?.*)/;

export const getCurrentHostPattern = (): string => {
	// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
	if (window.location.port !== '') {
		// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
		return `${window.location.protocol}//${window.location.hostname}:${window.location.port}`;
	}

	// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
	return `${window.location.protocol}//${window.location.hostname}`;
};

export const stripUrlToIssueKey = (tenantUrl?: string, candidateUrl?: string): string => {
	let tenantUrlPath = tenantUrl;
	if (tenantUrlPath === undefined || candidateUrl === undefined) {
		return candidateUrl || '';
	}

	if (!tenantUrlPath.endsWith('/')) {
		tenantUrlPath += '/';
	}

	tenantUrlPath += ISSUE_BROWSE_PATH;

	if (candidateUrl.indexOf(tenantUrlPath) !== 0) {
		return candidateUrl;
	}

	const candidatePath = candidateUrl.slice(tenantUrlPath.length);
	const match = candidatePath.match(ISSUE_KEY_REGEXP);

	if (match === null) {
		return candidateUrl;
	}

	return match[1] || ''; // issue key is at index 1 (first capture group)
};

export const stripQueryValueToIssueKey = (queryValue: string): string => {
	const currentHost = getCurrentHostPattern();
	return stripUrlToIssueKey(currentHost, queryValue);
};

export const transformIssueSuggestionItems = (
	results: IssueHistorySearchResults,
	formatMessage: FormatMessage,
	query = '',
): Options => {
	const { sections } = results;
	const heading = formatMessage(
		fg('jira-issue-terminology-refresh-m3')
			? messages.openIssuesIssueTermRefresh
			: messages.openIssues,
	);
	const noSuggestionsMessage = formatMessage(
		fg('jira-issue-terminology-refresh-m3')
			? messages.noHistoryResultsIssueTermRefresh
			: messages.noHistoryResults,
	);
	const suggestions = sections
		.map((section) => section.issues)
		.filter((issues) => !!issues) // eslint-disable-next-line @typescript-eslint/no-explicit-any
		.reduce<Array<any>>(
			(flatten, current) =>
				flatten.concat(
					current.map((issue) => ({
						label: `${issue.key} ${issue.summaryText}`,
						value: issue.key,
						id: issue.id ? issue.id.toString() : '',
						iconUrl: issue.img,
						// Allows us to force all items returned by the endpoint to be displayed.
						filterValues: [query],
					})),
				),
			[],
		) // eslint-disable-next-line @typescript-eslint/no-explicit-any
		.reduce<Array<any>>(
			(uniq, current) =>
				uniq.findIndex((item) => item.value === current.value) < 0
					? // eslint-disable-next-line jira/js/no-reduce-accumulator-spread
						[...uniq, current]
					: uniq,
			[],
		);

	if (suggestions.length) {
		const selectSuggestions: Group[] = [{ label: heading, options: suggestions }];
		return selectSuggestions;
	}
	const noSuggestion: Option[] = [
		{
			label: noSuggestionsMessage,
			isDisabled: true,
			value: '',
		},
	];
	return noSuggestion;
};
